import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { euro, fmtDate, apiError, pctIt } from "@/lib/api";
import { CalendarClock, Users, PiggyBank, X, AlertTriangle } from "lucide-react";

const COL = { rosso: "#EF4444", giallo: "#F59E0B", verde: "#10B981" };
const nomeMese = (m) => new Date(`${m}-01`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
const RUOLI = { acquisitore: "Agente acquisitore", venditore: "Agente venditore", direttore: "Direttore", segnalatore: "Segnalatore", agente: "Agente" };
const Stato = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s === "sicura" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>{s === "sicura" ? "Sicuro" : "Probabile"}</span>;

function Riquadro({ k, titolo, d }) {
  return (
    <div className="card-immo p-5" data-testid={`pi-box-${k}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] flex items-center gap-1.5"><CalendarClock size={14} /> {titolo}</div>
      <div className="mono text-3xl font-extrabold mt-2" data-testid={`pi-box-${k}-totale`}>{euro(d.totale)}</div>
      <div className="text-[11px] text-[var(--muted)]">lordo provvigioni</div>
      <div className="flex gap-4 mt-3 text-sm">
        <div><div className="text-[10px] uppercase font-bold text-emerald-600">Sicuri</div><div className="mono font-semibold" data-testid={`pi-box-${k}-sicuri`}>{euro(d.sicuri)}</div></div>
        <div><div className="text-[10px] uppercase font-bold text-orange-600">Probabili</div><div className="mono font-semibold" data-testid={`pi-box-${k}-probabili`}>{euro(d.probabili)}</div></div>
      </div>
    </div>
  );
}

function Copertura({ m }) {
  if (m.copertura_pct == null) return <div className="text-xs text-[var(--muted)]" data-testid={`pi-copertura-${m.mese}`}>Nessuna spesa fissa prevista</div>;
  const p = Math.min(m.copertura_pct, 100);
  return (
    <div className="min-w-[180px]" data-testid={`pi-copertura-${m.mese}`}>
      <div className="flex items-baseline gap-1.5"><span className="font-display text-2xl font-extrabold" style={{ color: COL[m.colore] }}>{Math.round(m.copertura_pct)}%</span><span className="text-[11px] text-[var(--muted)]">spese fisse coperte dagli incassi sicuri</span></div>
      <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p}%`, background: COL[m.colore], transition: "width 900ms ease" }} /></div>
    </div>
  );
}

function RigaIncasso({ r, i, mese }) {
  return (
    <tr className="border-t border-[var(--border)] align-top" data-testid={`pi-riga-${mese}-${i}`}>
      <td className="py-2 pr-2"><div className="font-semibold">{r.indirizzo || r.titolo}</div>{r.indirizzo && <div className="text-[11px] text-[var(--muted)]">{r.titolo}</div>}</td>
      <td className="py-2 pr-2 capitalize">{r.lato}</td>
      <td className="py-2 pr-2 whitespace-nowrap">{r.data ? fmtDate(r.data) : "—"}{r.ritardo && <div className="text-[10px] font-bold text-red-600">in ritardo</div>}{r.da_confermare && <div className="text-[10px] font-bold text-amber-600" data-testid={`pi-da-confermare-${mese}-${i}`}>data da confermare</div>}</td>
      <td className="py-2 pr-2"><Stato s={r.stato} /></td>
      <td className="py-2 pr-2 text-right mono font-semibold">{euro(r.lordo)}</td>
      <td className="py-2 pr-2 text-right mono text-slate-500">{euro(r.iva)}</td>
      <td className="py-2 pl-3 pr-2 text-xs">{r.quote.length ? r.quote.map((q, j) => <div key={j} className="whitespace-nowrap">{q.nome} <span className="text-[var(--muted)]">({RUOLI[q.ruolo] || q.ruolo})</span> <span className="mono">{euro(q.importo)}</span></div>) : <span className="text-[var(--muted)]">—</span>}</td>
      <td className="py-2 text-right mono font-bold">{euro(r.netto_agenzia)}</td>
    </tr>
  );
}

function Mese({ m }) {
  return (
    <div className="card-immo p-5" data-testid={`pi-mese-${m.mese}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <h3 className="font-bold text-lg capitalize">{nomeMese(m.mese)}</h3>
        <Copertura m={m} />
      </div>
      {m.righe.length === 0 ? <div className="text-sm text-[var(--muted)] py-2">Nessun incasso previsto.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-[11px] uppercase tracking-wide text-[var(--muted)] text-left"><th className="py-1.5">Immobile</th><th>Parte</th><th>Data prevista</th><th>Stato</th><th className="text-right">Lordo</th><th className="text-right">IVA da accantonare</th><th className="pl-3">Quote collaboratori</th><th className="text-right">Netto agenzia</th></tr></thead>
            <tbody>{m.righe.map((r, i) => <RigaIncasso key={i} r={r} i={i} mese={m.mese} />)}</tbody>
            <tfoot><tr className="border-t-2 border-[#111] font-bold" data-testid={`pi-totali-${m.mese}`}><td className="py-2" colSpan={4}>Totale mese</td><td className="text-right mono">{euro(m.lordo)}</td><td className="text-right mono">{euro(m.iva)}</td><td className="pl-3 mono text-xs">{euro(m.quote)}</td><td className="text-right mono">{euro(m.netto_agenzia)}</td></tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function DettaglioCollab({ c, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="pi-collab-dettaglio">
      <div className="bg-white w-full sm:max-w-xl max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><div className="font-bold">{c.nome} · quote prossimi 3 mesi</div><button data-testid="pi-collab-chiudi" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X size={18} /></button></div>
        {c.dettaglio.map((x, i) => (
          <div key={i} className="flex justify-between gap-3 py-2 border-t border-[var(--border)] text-sm">
            <div><div className="font-medium">{x.indirizzo || x.titolo}</div><div className="text-[11px] text-[var(--muted)] capitalize">{x.lato} · {RUOLI[x.ruolo] || x.ruolo} {pctIt(x.pct)} · {x.data ? fmtDate(x.data) : nomeMese(x.mese)}</div></div>
            <div className="text-right"><div className="mono font-semibold">{euro(x.importo)}</div><Stato s={x.stato} /></div>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}

function DaDividere({ d }) {
  const [sel, setSel] = useState(null);
  const mesi = d.mesi.slice(0, 3);
  return (
    <div className="card-immo p-5" data-testid="pi-da-dividere">
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED] mb-3">Da dividere · prossimi 3 mesi</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase text-[var(--muted)] mb-2 flex items-center gap-1.5"><Users size={13} /> Quote collaboratori da pagare</div>
          {d.collaboratori.length === 0 ? <div className="text-sm text-[var(--muted)]">Nessuna quota prevista.</div> : d.collaboratori.map((c, i) => (
            <button key={i} data-testid={`pi-collab-${i}`} onClick={() => setSel(c)} className="w-full flex items-center justify-between gap-3 py-2 border-t border-[var(--border)] text-sm text-left hover:bg-[#FC82ED]/5 px-1 rounded">
              <span className="font-medium min-w-0 break-words">{c.nome} <span className="text-[11px] text-[var(--muted)]">{RUOLI[c.ruolo] || c.ruolo}</span></span>
              <span className="shrink-0 text-right text-xs flex flex-col sm:flex-row sm:gap-1 whitespace-nowrap"><span className="text-emerald-700">sicure <b className="mono">{euro(c.sicure)}</b></span><span className="hidden sm:inline">·</span><span className="text-orange-700">probabili <b className="mono">{euro(c.probabili)}</b></span></span>
            </button>
          ))}
        </div>
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase text-[var(--muted)] mb-2 flex items-center gap-1.5"><PiggyBank size={13} /> Margine e quota per socio ({d.numero_soci} soci)</div>
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[460px] whitespace-nowrap">
            <thead><tr className="text-[11px] uppercase text-[var(--muted)] text-left"><th className="py-1">Mese</th><th className="text-right">Netto agenzia</th><th className="text-right">Spese fisse</th><th className="text-right">Margine</th><th className="text-right">Per socio</th></tr></thead>
            <tbody>{mesi.map((m) => (
              <tr key={m.mese} className="border-t border-[var(--border)]" data-testid={`pi-margine-${m.mese}`}>
                <td className="py-1.5 capitalize">{nomeMese(m.mese)}</td><td className="text-right mono">{euro(m.netto_agenzia)}</td><td className="text-right mono">−{euro(m.fisse_previste)}</td>
                <td className={`text-right mono font-semibold ${m.margine >= 0 ? "text-emerald-600" : "text-red-600"}`}>{euro(m.margine)}</td>
                <td className="text-right mono font-bold" data-testid={`pi-socio-${m.mese}`}>{m.quota_socio != null ? euro(m.quota_socio) : "—"}</td>
              </tr>
            ))}</tbody>
          </table></div>
          <p className="text-[11px] text-[var(--muted)] mt-1">Margine = netto agenzia (sicuri + probabili) − spese fisse previste. Quota per socio solo se il margine è positivo.</p>
        </div>
      </div>
      {sel && <DettaglioCollab c={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

export default function FinanzaProssimi() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/finanza/prossimi-incassi").then((r) => setD(r.data)).catch((e) => setErr(apiError(e))); }, []);
  if (err) return <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">{err}</div>;
  if (!d) return <div className="skeleton h-64 rounded-2xl" />;
  return (
    <div className="space-y-4" data-testid="finanza-prossimi-incassi">
      <p className="text-xs text-[var(--muted)]">Stessi dati di Provvigioni future e della tab Provvigioni degli immobili. Sicuri = preliminare firmato o proposta accettata senza condizione; probabili = subordinati a mutuo. Quando l'incasso viene abbinato a una riga dell'estratto conto esce da qui e conta nel mese reale.</p>
      <div className="grid md:grid-cols-3 gap-3">
        <Riquadro k="questo-mese" titolo="In arrivo questo mese" d={d.riquadri.questo_mese} />
        <Riquadro k="prossimo-mese" titolo="Prossimo mese" d={d.riquadri.prossimo_mese} />
        <Riquadro k="entro-90" titolo="Entro 90 giorni" d={d.riquadri.entro_90} />
      </div>
      <DaDividere d={d} />
      {d.mesi.map((m) => <Mese key={m.mese} m={m} />)}
      {d.senza_data.length > 0 && (
        <div className="card-immo p-5" data-testid="pi-senza-data">
          <div className="text-sm font-bold flex items-center gap-1.5 text-amber-700 mb-2"><AlertTriangle size={15} /> Senza data prevista (non conteggiati nei mesi)</div>
          {d.senza_data.map((r, i) => <div key={i} className="text-sm py-1 border-t border-[var(--border)] flex justify-between gap-3"><span>{r.indirizzo || r.titolo} · <span className="capitalize">{r.lato}</span> · {r.avviso}</span><span className="mono">{euro(r.lordo)}</span></div>)}
        </div>
      )}
    </div>
  );
}
